import { useEffect, useRef } from 'react';
import OpenSeadragon from 'openseadragon';
import type { OSDHandle } from './createViewer';
import { useMapStore } from '../store/useMapStore';
import { clusterZoomFromViewer, viewerZoomFromCluster, toUVTL, uvToLngLat } from './coords';
import { buildClusterIndex, featureToImageXY } from '../logic/cluster';

const IMG_W = 8192;
const IMG_H = 6656;

const factionColor: Record<string, string> = {
  Imperium: '#4A90E2',
  Chaos: '#E24A4A',
  Xenos: '#77C159',
  Other: '#B0B0B0',
};

export default function CanvasOverlay({ osd }: { osd: OSDHandle }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const points = useMapStore((s) => s.points);
  const filters = useMapStore((s) => s.filters);
  const clusteringEnabled = useMapStore((s) => s.clusteringEnabled);
  const selectedId = useMapStore((s) => s.selectedId);
  const setSelectedId = useMapStore((s) => s.setSelectedId);
  const setSidebarOpen = useMapStore((s) => s.setSidebarOpen);

  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.style.position = 'absolute';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '5';
    // Append to the viewer container, not the internal <canvas> element.
    const parent = (osd.viewer as any).element || (osd.viewer as any).container || osd.viewer.canvas?.parentElement;
    (parent || osd.viewer.canvas).appendChild(canvas);
    canvasRef.current = canvas;

    let drawn: { x: number; y: number; r: number; type: 'point'|'cluster'; id?: string; count?: number; }[] = [];

    const toScreen = (item: OpenSeadragon.TiledImage, xTL: number, yTL: number) => {
      const imgPt = new OpenSeadragon.Point(xTL, yTL);
      const vp = osd.viewer.viewport.pixelFromPoint(item.imageToViewportCoordinates(imgPt), true);
      return { x: vp.x, y: vp.y };
    };

    // Build cluster index for current filtered set once per effect run
    const allowed = points.filter((p) => {
      if (filters.faction.size && !filters.faction.has(p.faction)) return false;
      if (filters.segmentum.size && !filters.segmentum.has(p.segmentum || 'Unknown')) return false;
      if (filters.type.size && (!p.type || !filters.type.has(p.type))) return false;
      return true;
    });
    const allowedWithCoords = allowed.map((p) => {
      const { uTL, vTL } = toUVTL(p.xTL, p.yTL);
      const { lng, lat } = uvToLngLat(uTL, vTL);
      return { record: p, lng, lat };
    });
    const index = clusteringEnabled && allowed.length ? buildClusterIndex(allowed) : null;

    const draw = () => {
      const item = osd.getItem();
      if (!item || !canvasRef.current) return;
      const c = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      const elem = (osd.viewer as any).element || (osd.viewer as any).container || c.parentElement!;
      const w = (elem as HTMLElement).clientWidth;
      const h = (elem as HTMLElement).clientHeight;
      c.width = Math.round(w * dpr);
      c.height = Math.round(h * dpr);
      c.style.width = `${w}px`;
      c.style.height = `${h}px`;

      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, c.width, c.height);

      // Compute viewport bbox in image coordinates (top-left origin)
      const b = osd.viewer.viewport.getBounds(true);
      const tl = item.viewportToImageCoordinates(new OpenSeadragon.Point(b.x, b.y));
      const br = item.viewportToImageCoordinates(new OpenSeadragon.Point(b.x + b.width, b.y + b.height));
      const u1 = tl.x / IMG_W, v1 = tl.y / IMG_H;
      const u2 = br.x / IMG_W, v2 = br.y / IMG_H;
      const minLng = Math.min(u1, u2) * 360 - 180;
      const maxLng = Math.max(u1, u2) * 360 - 180;
      const minLat = 85 - Math.max(v1, v2) * 170;
      const maxLat = 85 - Math.min(v1, v2) * 170;

      const z = clusterZoomFromViewer(item, osd.viewer);
      const features = clusteringEnabled && index
        ? (index.getClusters([minLng, minLat, maxLng, maxLat], z) as any[])
        : allowedWithCoords
            .filter(({ lng, lat }) => lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat)
            .map(({ record, lng, lat }) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [lng, lat] as [number, number] },
              properties: {
                id: record.id,
                faction: record.faction,
                name: record.name,
                xTL: record.xTL,
                yTL: record.yTL,
              },
            }));

      drawn = [];
      for (const f of features) {
        const { xTL, yTL } = featureToImageXY(f);
        const { x, y } = toScreen(item, xTL, yTL);
        if (f.properties.cluster) {
          const count = f.properties.point_count as number;
          const r = Math.max(10, Math.min(28, 10 + Math.log2(count + 1) * 6));
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(0,0,0,0.5)';
          ctx.fill();
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#FFFFFF';
          ctx.stroke();
          ctx.fillStyle = '#FFFFFF';
          ctx.font = 'bold 12px system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(count), x, y);
          drawn.push({ x, y, r, type: 'cluster', count, id: String(f.properties.cluster_id) });
        } else {
          const color = factionColor[f.properties.faction] || '#FFFFFF';
          const r = 4;
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
          ctx.lineWidth = 1;
          ctx.strokeStyle = '#000000';
          ctx.stroke();
          drawn.push({ x, y, r, type: 'point', id: f.properties.id });
        }
      }

      // Highlight selected world
      if (selectedId) {
        const rec = points.find((p) => p.id === selectedId);
        if (rec) {
          const { x, y } = toScreen(item, rec.xTL, rec.yTL);
          ctx.beginPath();
          ctx.arc(x, y, 8, 0, Math.PI * 2);
          ctx.strokeStyle = '#FFD166';
          ctx.lineWidth = 2.5;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(x, y, 12, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(255,209,102,0.35)';
          ctx.lineWidth = 3;
          ctx.stroke();
        }
      }
    };

    const schedule = () => requestAnimationFrame(draw);
    osd.viewer.addHandler('open', schedule);
    osd.viewer.addHandler('animation', schedule);
    osd.viewer.addHandler('update-viewport', schedule);
    osd.viewer.addHandler('resize', schedule);
    // Trigger at least once after mount
    schedule();

    // Hover/click handling
    canvas.style.pointerEvents = 'none';
    const tooltip = document.createElement('div');
    tooltip.style.position = 'absolute';
    tooltip.style.pointerEvents = 'none';
    tooltip.style.background = 'rgba(0,0,0,0.8)';
    tooltip.style.color = 'white';
    tooltip.style.padding = '2px 6px';
    tooltip.style.fontSize = '12px';
    tooltip.style.borderRadius = '4px';
    tooltip.style.transform = 'translate(8px, -8px)';
    tooltip.style.display = 'none';
    const evtTarget = canvas.parentElement || document.body;
    evtTarget.appendChild(tooltip);

    function onMove(e: MouseEvent) {
      const rect = (evtTarget as HTMLElement).getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      let best: { d2: number; item: typeof drawn[number] } | null = null;
      for (const it of drawn) {
        const dx = it.x - x; const dy = it.y - y; const d2 = dx*dx + dy*dy;
        const r2 = (it.r + 6) * (it.r + 6);
        if (d2 <= r2) {
          if (!best || d2 < best.d2) best = { d2, item: it };
        }
      }
      if (best) {
        const it = best.item;
        tooltip.style.left = `${e.clientX}px`;
        tooltip.style.top = `${e.clientY}px`;
        if (it.type === 'cluster') {
          tooltip.textContent = `${it.count} worlds`;
        } else {
          const rec = points.find((p) => p.id === it.id);
          if (rec) {
            const parts = [rec.name];
            const meta = [rec.faction, rec.type, rec.segmentum].filter(Boolean).join(' • ');
            if (meta) parts.push(`\n${meta}`);
            tooltip.textContent = parts.join('');
          } else {
            tooltip.textContent = 'World';
          }
        }
        tooltip.style.display = 'block';
      } else {
        tooltip.style.display = 'none';
      }
    }

    function onLeave() { tooltip.style.display = 'none'; }

    function onClick(e: MouseEvent) {
      const rect = (evtTarget as HTMLElement).getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      let best: { d2: number; item: typeof drawn[number] } | null = null;
      for (const it of drawn) {
        const dx = it.x - x; const dy = it.y - y; const d2 = dx*dx + dy*dy;
        const r2 = (it.r + 6) * (it.r + 6);
        if (d2 <= r2) {
          if (!best || d2 < best.d2) best = { d2, item: it };
        }
      }
      if (!best) return;
      const item = osd.getItem(); if (!item) return;
      if (best.item.type === 'cluster') {
        const clusterId = Number(best.item.id);
        // Supercluster expansion zoom approximation: zoom in a couple levels
        const z = clusterZoomFromViewer(item, osd.viewer) + 2;
        const vpZoom = viewerZoomFromCluster(item, osd.viewer, z);
        // Center at the clicked screen point
        const vpPoint = osd.viewer.viewport.pointFromPixel(new OpenSeadragon.Point(best.item.x, best.item.y), true);
        osd.viewer.viewport.zoomTo(vpZoom, vpPoint);
      } else {
        // Select and center
        const id = best.item.id as string;
        const vpPoint = osd.viewer.viewport.pointFromPixel(new OpenSeadragon.Point(best.item.x, best.item.y), true);
        osd.viewer.viewport.panTo(vpPoint);
        setSelectedId(id);
        setSidebarOpen(true);
      }
    }

    (evtTarget as HTMLElement).addEventListener('mousemove', onMove);
    (evtTarget as HTMLElement).addEventListener('mouseleave', onLeave);
    (evtTarget as HTMLElement).addEventListener('click', onClick);

    return () => {
      osd.viewer.removeHandler('open', schedule);
      osd.viewer.removeHandler('animation', schedule);
      osd.viewer.removeHandler('update-viewport', schedule);
      osd.viewer.removeHandler('resize', schedule);
      canvas.remove();
      tooltip.remove();
      (evtTarget as HTMLElement).removeEventListener('mousemove', onMove);
      (evtTarget as HTMLElement).removeEventListener('mouseleave', onLeave);
      (evtTarget as HTMLElement).removeEventListener('click', onClick);
    };
  }, [osd, points, filters, selectedId, setSelectedId, setSidebarOpen, clusteringEnabled]);

  return null;
}
